import React, { useEffect, useState, useRef } from 'react';
import { Shield, Crosshair } from 'lucide-react';
import { AUDIO_PATHS } from '../config/gameConstants';

/**
 * AAPurchasePopup - Shows when a country purchases AA Defense
 * Features: Country flag, AA tier badge, cost display, cash register sound
 */
export default function AAPurchasePopup({ 
  countryCode, 
  countryName, 
  tier,
  cost, 
  successRate,
  onComplete 
}) {
  const [visible, setVisible] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    // Show popup
    setVisible(true);

    // Play cash register sound
    if (audioRef.current) {
      audioRef.current.volume = 0.5;
      audioRef.current.play().catch(err => console.warn('Cash register audio failed:', err));
    }

    // Auto-hide after 5 seconds
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(() => {
        onComplete && onComplete();
      }, 500);
    }, 5000);

    return () => clearTimeout(timer);
  }, [onComplete]);

  // Country flag emojis
  const FLAG_EMOJIS = {
    'USA': '🇺🇸',
    'RUS': '🇷🇺',
    'CHN': '🇨🇳',
    'EU': '🇪🇺',
    'NK': '🇰🇵'
  };

  const flag = FLAG_EMOJIS[countryCode] || '🏳️';

  // AA Tier badge images (larger, more legible sprites)
  const tierBadge = `/assets/aa-tier-${tier}.png`;
  
  // Country-specific missile sprites
  const MISSILE_SPRITES = {
    'USA': '/assets/aa-missiles/stinger.png',
    'RUS': '/assets/aa-missiles/strela-2.png',
    'CHN': '/assets/aa-missiles/fn-6.png',
    'NK': '/assets/aa-missiles/ht-16pgj.png',
    'EU': '/assets/aa-missiles/mistral.png'
  };
  const missileSprite = MISSILE_SPRITES[countryCode] || MISSILE_SPRITES['USA'];

  return (
    <>
      {/* Cash register audio */}
      <audio ref={audioRef} src={AUDIO_PATHS.cashRegister} preload="auto" />

      {/* Popup overlay */}
      <div 
        className={`fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none transition-opacity duration-500 ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {/* Background blur */}
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

        {/* Popup card */}
        <div 
          className={`relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 border-4 border-blue-400 rounded-2xl p-8 shadow-2xl transform transition-all duration-500 ${
            visible ? 'scale-100 rotate-0' : 'scale-50 rotate-12'
          }`}
          style={{
            boxShadow: '0 0 60px rgba(59, 130, 246, 0.8), 0 0 120px rgba(59, 130, 246, 0.4)',
            minWidth: '450px'
          }}
        >
          {/* Shield symbols floating around */}
          <div className="absolute -top-4 -left-4 text-6xl animate-pulse">
            🛡️
          </div>
          <div className="absolute -top-4 -right-4 text-6xl animate-pulse" style={{ animationDelay: '0.3s' }}>
            🚀
          </div>
          <div className="absolute -bottom-4 -left-4 text-6xl animate-pulse" style={{ animationDelay: '0.6s' }}>
            🎯
          </div>
          <div className="absolute -bottom-4 -right-4 text-6xl animate-pulse" style={{ animationDelay: '0.9s' }}>
            🛡️
          </div>

          {/* Crosshair icons */}
          <div className="absolute top-1/2 left-4 transform -translate-y-1/2">
            <Crosshair className="w-12 h-12 text-red-400 animate-pulse" />
          </div>
          <div className="absolute top-1/2 right-4 transform -translate-y-1/2">
            <Crosshair className="w-12 h-12 text-red-400 animate-pulse" style={{ animationDelay: '0.5s' }} />
          </div>

          {/* Content */}
          <div className="relative z-10 text-center space-y-4">
            {/* Flag and country name */}
            <div className="flex items-center justify-center gap-3">
              <span className="text-7xl">{flag}</span>
              <div>
                <div className="text-3xl font-bold text-white">{countryName}</div>
                <div className="text-sm text-blue-300 uppercase tracking-wider">AA Defense Purchase</div>
              </div>
            </div>

            {/* AA Tier Badge - Large, legible sprite */}
            <div className="bg-blue-950/50 rounded-xl p-6 border-2 border-blue-400 flex flex-col items-center justify-center gap-3">
              <img 
                src={tierBadge} 
                alt={`AA Tier ${tier}`} 
                className="w-32 h-32 object-contain drop-shadow-2xl"
                style={{
                  filter: 'drop-shadow(0 0 20px rgba(59, 130, 246, 0.8))'
                }}
              />
              <div className="flex items-center justify-center gap-3">
                <Shield className="w-8 h-8 text-blue-400" />
                <div className="text-4xl font-bold text-blue-400">
                  TIER {tier}
                </div>
                <Shield className="w-8 h-8 text-blue-400" />
              </div>
              <div className="text-sm text-blue-200">
                Anti-Aircraft Defense System
              </div>
              
              {/* Country-Specific Missile Sprite */}
              <div className="mt-3 pt-3 border-t border-blue-500/30 w-full">
                <div className="text-xs text-blue-300 uppercase tracking-wider text-center mb-2">
                  Missile Type
                </div>
                <div className="flex justify-center">
                  <img 
                    src={missileSprite}
                    alt={`${countryName} AA Missile`}
                    className="h-20 w-auto object-contain drop-shadow-xl"
                    style={{
                      filter: 'drop-shadow(0 0 15px rgba(96, 165, 250, 0.7))'
                    }}
                  />
                </div>
                <div className="text-xs text-blue-400 text-center mt-2 font-mono">
                  {countryCode === 'USA' && 'FIM-92 Stinger'}
                  {countryCode === 'RUS' && 'Strela-2'}
                  {countryCode === 'CHN' && 'FN-6'}
                  {countryCode === 'NK' && 'HT-16PGJ'}
                  {countryCode === 'EU' && 'Mistral'}
                </div>
              </div>
            </div>

            {/* Success Rate */}
            <div className="text-xl text-green-400 font-bold">
              🎯 Success Rate: {successRate}%
            </div>

            {/* Cost */}
            <div className="text-xl text-yellow-400 font-bold">
              💰 Cost: ${cost}M
            </div>

            {/* Progress bar showing tier level */}
            <div className="w-full bg-gray-800 h-4 rounded-full overflow-hidden border-2 border-blue-500">
              <div 
                className="h-full bg-gradient-to-r from-blue-500 via-cyan-500 to-blue-600 transition-all duration-1000"
                style={{ width: `${(tier / 3) * 100}%` }}
              />
            </div>
          </div>

          {/* Glowing particles effect */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
            {[...Array(20)].map((_, i) => (
              <div
                key={`particle-aa-${i}`}
                className="absolute w-2 h-2 bg-blue-400 rounded-full opacity-60"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animation: `float-particle ${2 + Math.random() * 3}s ease-in-out infinite`,
                  animationDelay: `${Math.random() * 2}s`
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* CSS for particle animation */}
      <style jsx>{`
        @keyframes float-particle {
          0%, 100% {
            transform: translate(0, 0) scale(1);
            opacity: 0.6;
          }
          50% {
            transform: translate(${Math.random() * 40 - 20}px, ${Math.random() * 40 - 20}px) scale(1.5);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
}
